import React, { useState } from "react";
import { FaEye, FaEyeSlash, FaCloudUploadAlt } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function AdminSellerReg() {
  const { AdminSellerRegister } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    companyLocation: "",
    companyEmail: "",
    companyLogo: null,
    termsAccepted: false,
  });

  const [fileName, setFileName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [apiErrors, setApiErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear field-specific errors on change
    setFieldErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
    setApiErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      setFormData((prev) => ({ ...prev, companyLogo: file }));
      setFieldErrors((prev) => ({ ...prev, companyLogo: false }));
      setApiErrors((prev) => ({ ...prev, companyLogo: false }));
    }
  };

  const isFormValid = () => {
    return (
      formData.name &&
      formData.email &&
      formData.password &&
      formData.confirmPassword &&
      formData.companyName &&
      formData.companyLocation &&
      formData.companyEmail &&
      formData.companyLogo &&
      formData.termsAccepted
    );
  };

  const handleRegister = async (e) => {
    e.preventDefault();
  
    setFieldErrors({});
    setApiErrors({});
  
    const newFieldErrors = {};
    if (!formData.name) newFieldErrors.name = "Name is required";
    if (!formData.email) newFieldErrors.email = "Email is required";
    if (!formData.password) newFieldErrors.password = "Password is required";
    if (!formData.confirmPassword) newFieldErrors.confirmPassword = "Please confirm password";
    if (!formData.companyName) newFieldErrors.companyName = "Company name is required";
    if (!formData.companyLocation) newFieldErrors.companyLocation = "Company location is required";
    if (!formData.companyEmail) newFieldErrors.companyEmail = "Company email is required";
    if (!formData.companyLogo) newFieldErrors.companyLogo = "Company logo is required";
  
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      return;
    }
  
    if (formData.password !== formData.confirmPassword) {
      setFieldErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      return;
    }
  
    if (!formData.termsAccepted) {
      setApiErrors({ general: "You must accept the terms and conditions" });
      return;
    }
  
    setIsLoading(true);
  
    const formPayload = new FormData();
    formPayload.append("username", formData.name);
    formPayload.append("email", formData.email);
    formPayload.append("password", formData.password);
    formPayload.append("confirm_password", formData.confirmPassword);
    formPayload.append("company.name", formData.companyName);
    formPayload.append("company.mail_id", formData.companyEmail);
    formPayload.append("company.location", formData.companyLocation);
    formPayload.append("company.logo", formData.companyLogo);
  
    try {
      const result = await AdminSellerRegister(formPayload);
  
      if (result.success) {
        toast.success("Registration successful!", {
          position: "top-center",
          autoClose: 3000,
        });
  
        // ✅ Reset form
        setFormData({
          name: "",
          email: "",
          password: "",
          confirmPassword: "",
          companyName: "",
          companyLocation: "",
          companyEmail: "",
          companyLogo: null,
          termsAccepted: false,
        });
        setFileName("");
      } else {
        const errors = typeof result.message === "object"
          ? result.message
          : { general: result.message };
  
        setApiErrors(errors);
        toast.error("Registration failed. Please check the errors.", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      setApiErrors({ general: "Something went wrong. Please try again." });
      toast.error("Something went wrong. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  

  return (
    <div className="flex justify-center items-center mt-5">
      {/* Add ToastContainer with top-center position and 3s autoClose */}
      <ToastContainer position="top-center" autoClose={3000} />
      
      <div className="bg-gray-100 rounded-xl shadow-lg p-12 w-full">
        

        {/* Display general API errors */}
        {apiErrors.general && (
          <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-lg">
            {apiErrors.general}
          </div>
        )}

        <form onSubmit={handleRegister} encType="multipart/form-data">
          {/* ... rest of your form code remains exactly the same ... */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Personal Info Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Personal Info</h3>
              <div className="mb-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${fieldErrors.name || apiErrors.name ? "border-red-500" : ""}`}
                />
                {(fieldErrors.name || apiErrors.name) && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.name || apiErrors.name}</p>
                )}
              </div>
              
              <div className="mb-4">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${fieldErrors.email || apiErrors.email ? "border-red-500" : ""}`}
                />
                {(fieldErrors.email || apiErrors.email) && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.email || apiErrors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-lg ${fieldErrors.password || apiErrors.password ? "border-red-500" : ""}`}
                  />
                  <span
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-3 cursor-pointer"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                {(fieldErrors.password || apiErrors.password) && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.password || apiErrors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full p-2 border rounded-lg ${fieldErrors.confirmPassword ? "border-red-500" : ""}`}
                  />
                  <span
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute right-3 top-3 cursor-pointer"
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                {fieldErrors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* Company Info Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Company Info</h3>
              <div className="mb-4">
                <input
                  type="text"
                  name="companyName"
                  placeholder="Company Name"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${fieldErrors.companyName || apiErrors.companyName ? "border-red-500" : ""}`}
                />
                {(fieldErrors.companyName || apiErrors.companyName) && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.companyName || apiErrors.companyName}</p>
                )}
              </div>
              
              <div className="mb-4">
                <input
                  type="text"
                  name="companyLocation"
                  placeholder="Company Location"
                  value={formData.companyLocation}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${fieldErrors.companyLocation || apiErrors.companyLocation ? "border-red-500" : ""}`}
                />
                {(fieldErrors.companyLocation || apiErrors.companyLocation) && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.companyLocation || apiErrors.companyLocation}</p>
                )}
              </div>
              
              <div className="mb-4">
                <input
                  type="email"
                  name="companyEmail"
                  placeholder="Company Email"
                  value={formData.companyEmail}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-lg ${fieldErrors.companyEmail || apiErrors.companyEmail ? "border-red-500" : ""}`}
                />
                {(fieldErrors.companyEmail || apiErrors.companyEmail) && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.companyEmail || apiErrors.companyEmail}</p>
                )}
              </div>

              {/* File Upload */}
              <div className="mb-4">
                <label className="cursor-pointer block">
                  <div className={`w-full p-2 border bg-white rounded-lg ${fieldErrors.companyLogo || apiErrors.companyLogo ? "border-red-500" : "border-gray-300"}`}>
                    <input
                      type="file"
                      name="companyLogo"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <div className="flex items-center justify-between">
                      <span className={`${!fileName ? "text-gray-400" : "text-gray-800"}`}>
                        {fileName || "Company Logo"}
                      </span>
                      <FaCloudUploadAlt className="text-gray-500" />
                    </div>
                  </div>
                </label>
                {(fieldErrors.companyLogo || apiErrors.companyLogo) && (
                  <p className="text-red-500 text-sm mt-1">{fieldErrors.companyLogo || apiErrors.companyLogo}</p>
                )}
              </div>
              <div className="flex items-center mt-4">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <label>I agree to the terms & policy.</label>
              </div>
              {apiErrors.termsAccepted && (
                <p className="text-red-500 text-sm mt-1">{apiErrors.termsAccepted}</p>
              )}
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <button
              type="submit"
              className="w-64 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              disabled={isLoading}
            >
              {isLoading ? "Registering..." : "Sign Up"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminSellerReg;





